import type { ReactNode } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "date-fns";
import { convertToCurrency } from "creso-core/utils";
import type { SubmittedOrder } from "creso-core/types";
import { AmcLogo } from "../../logos";
import {
  OrderStatusBadge,
  OrderTypeBadge,
  ProductTypeBadge,
} from "../../badges";
import { Units } from "../../primitives";

export type OrdersColumnOptions = {
  /** Wraps the scheme name, e.g. in a fund-detail link. */
  renderSchemeLink?: (args: {
    fundId: string;
    children: ReactNode;
  }) => ReactNode;
  /** Row action menu. Omit it and no action column is produced. */
  renderActions?: (order: SubmittedOrder) => ReactNode;
};

export function createOrdersColumns({
  renderSchemeLink,
  renderActions,
}: OrdersColumnOptions = {}) {
  const helper = createColumnHelper<SubmittedOrder>();

  return [
    helper.accessor("scheme_name", {
      header: "Scheme name",
      cell: (info) => {
        const fundId = info.row.original.fund_id;

        const label = (
          <div>
            <span className="text-sm">{info.getValue()}</span>
            <ProductTypeBadge
              className="ml-2"
              type={info.row.original.product_type}
            />
          </div>
        );

        return (
          <div className="flex flex-row gap-2 items-center">
            <AmcLogo amc_code={info.row.original.amc_morningstar_code} />
            {fundId && renderSchemeLink
              ? renderSchemeLink({ fundId, children: label })
              : label}
          </div>
        );
      },
      minSize: 250,
    }),
    helper.accessor("created_at", {
      header: "Placed on",
      cell: (info) => (
        <div className="flex flex-col gap-1">
          <span className="text-sm">
            {formatDate(new Date(info.getValue()), "dd MMM yyyy")}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDate(new Date(info.getValue()), "hh:mm a")}
          </span>
        </div>
      ),
      minSize: 100,
    }),
    helper.accessor("type", {
      header: "Order type",
      cell: (info) => <OrderTypeBadge type={info.getValue()} />,
    }),
    helper.accessor("amount", {
      header: "Amount/Units",
      cell: (info) => (
        <div className="flex flex-col gap-1">
          {info.row.original.amount && info.row.original.units ? (
            <>
              <span className="text-sm">
                {convertToCurrency(info.getValue())}
              </span>
              <span className="text-xs text-muted-foreground">
                <Units>{info.row.original.units}</Units>
              </span>
            </>
          ) : (
            <span className="text-sm">
              {info.row.original.amount ? (
                convertToCurrency(info.getValue())
              ) : info.row.original.units ? (
                <Units>{info.row.original.units}</Units>
              ) : (
                "-"
              )}
            </span>
          )}
        </div>
      ),
    }),
    helper.accessor("order_status", {
      header: "Status",
      cell: (info) => <OrderStatusBadge status={info.getValue()} />,
    }),
    helper.accessor("order_remark", {
      header: "Remarks",
      cell: (info) => <span className="text-sm">{info.getValue()}</span>,
    }),
    ...(renderActions
      ? [
          helper.display({
            id: "actions",
            header: "",
            cell: (info) => (
              <div className="text-right">
                {renderActions(info.row.original)}
              </div>
            ),
            size: 50,
          }),
        ]
      : []),
  ];
}
